import moment, { Moment } from 'moment'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiBadge, EuiButton, EuiFlexGroup, EuiFlexItem, EuiLink, EuiPage, EuiPageBody, EuiPageHeader, EuiPageHeaderSection, EuiPanel, EuiText, EuiTitle, SortDirection } from '@elastic/eui'
import { EuiBasicTableColumn } from '@elastic/eui/src/components/basic_table/basic_table'

import { AIPlanBlueprintStatus } from 'api/interfaces/AIPlanBlueprint'
import { useDeleteAIPlanBlueprintMutation, useGetAIPlanBlueprintsQuery, useUnhideAIPlanBlueprintMutation } from 'api/rtkQueryApi/platform/aiPlanBlueprintsApi'
import { RootState } from 'app/rootReducer'
import { AcEuiInMemoryTableWithSearch } from 'components/Basic/AcEuiInMemoryTableWithSearch'
import { reset } from 'features/planner/CreateAIPlanBlueprintSlice'
import AgencyV2ModalOld from 'features/planner/old-pages/components/AgencyV2ModalOld'

import history from '../../services/HistoryService'

interface AIPlanBlueprintItem {
  id: string
  name: string
  status: AIPlanBlueprintStatus
  created: Moment
  isHidden: boolean
}

const getStatusText = (status: AIPlanBlueprintStatus) => {
  switch (status) {
    case AIPlanBlueprintStatus.Processing:
      return 'Processing'
    case AIPlanBlueprintStatus.Ready:
      return 'Ready'
    case AIPlanBlueprintStatus.Deleted:
      return 'Deleted'
    case AIPlanBlueprintStatus.Disapproved:
      return 'Disapproved'
  }
}

const getStatusBadgeColor = (status: AIPlanBlueprintStatus) => {
  switch (status) {
    case AIPlanBlueprintStatus.Processing:
      return 'warning'
    case AIPlanBlueprintStatus.Ready:
      return 'success'
    case AIPlanBlueprintStatus.Deleted:
    case AIPlanBlueprintStatus.Disapproved:
      return 'danger'
  }
}

const StatusBadge = (status: AIPlanBlueprintStatus) => {
  return <EuiBadge color={getStatusBadgeColor(status)}>{getStatusText(status)}</EuiBadge>
}

export const AIPlanBlueprintsPage: React.FC = () => {
  const dispatch = useDispatch()
  const { currentOrganization, currentAccount, isStaffUser, showInternalView } = useSelector((state: RootState) => state.app)
  const { data: blueprints, isFetching: isFetchingBlueprints } = useGetAIPlanBlueprintsQuery({ organizationId: currentOrganization?.id ?? '', showHidden: isStaffUser && showInternalView })
  const [deleteAIPlanBlueprint, deleteAIPlanBlueprintResult] = useDeleteAIPlanBlueprintMutation()

  const [blueprintItems, setBlueprintItems] = useState<AIPlanBlueprintItem[]>([])
  const [processingModalOpen, setProcessingModalOpen] = useState(false)
  const [selectedBlueprintForDelete, setSelectedBlueprintForDelete] = useState<AIPlanBlueprintItem | undefined>()
  const [selectedBlueprintForUnhide, setSelectedBlueprintForUnhide] = useState<AIPlanBlueprintItem | undefined>()
  const [unhideBlueprint, unhideBlueprintResult] = useUnhideAIPlanBlueprintMutation()

  useEffect(() => {
    if (isFetchingBlueprints || !blueprints || !currentAccount) {
      return
    }

    const items = blueprints
      .filter(b => b.accountId === currentAccount?.id)
      .map(b => ({
        id: b.id,
        name: b.name,
        status: b.status,
        created: moment(b.created),
        isHidden: b.isHidden ?? false
      }))

    if (items.length > 0) {
      setBlueprintItems(items)
    } else {
      dispatch(reset())
      history.push('/ai/blueprints/welcome')
    }
  }, [blueprints, currentAccount, dispatch, isFetchingBlueprints])

  const onBlueprintViewClick = (item: AIPlanBlueprintItem) => {
    if (!isStaffUser && item.status === AIPlanBlueprintStatus.Processing) {
      setProcessingModalOpen(true)
      return
    }

    history.push(`/ai/blueprints/${item.id}`)
  }

  const columns: EuiBasicTableColumn<AIPlanBlueprintItem>[] = [
    {
      name: 'Blueprint Name',
      sortable: (item: AIPlanBlueprintItem) => item.name,
      render: (item: AIPlanBlueprintItem) => {
        return (
          <React.Fragment>
            <EuiLink onClick={() => onBlueprintViewClick(item)} color='success'>
              {item.name}
            </EuiLink>
            {item.isHidden && isStaffUser && (
              <>
                {' '}
                <EuiBadge color='accent' onClick={() => setSelectedBlueprintForUnhide(item)} onClickAriaLabel='Make visible'>
                  Hidden
                </EuiBadge>
              </>
            )}
          </React.Fragment>
        )
      }
    },
    {
      name: 'Date Created',
      field: 'created',
      render: (created: Moment) => {
        return moment(created).format('MM/DD/YYYY')
      },
      sortable: true
    },
    {
      name: 'Status',
      field: 'status',
      sortable: true,
      render: (status: AIPlanBlueprintStatus) => {
        return StatusBadge(status)
      }
    },
    {
      name: 'Actions',
      actions: [
        {
          name: 'Delete',
          description: 'Delete this blueprint',
          icon: 'trash',
          type: 'icon',
          color: 'danger',
          onClick: (item: AIPlanBlueprintItem) => {
            setSelectedBlueprintForDelete(item)
          },
          enabled: (item: AIPlanBlueprintItem) => item.status === AIPlanBlueprintStatus.Ready,
          available: () => !deleteAIPlanBlueprintResult.isLoading
        }
      ]
    }
  ]

  const sorting = {
    allowNeutralSort: true,
    enableAllColumns: true,
    sort: {
      field: 'created',
      direction: SortDirection.DESC
    }
  }

  const onDeleteBlueprintClick = async () => {
    if (!currentAccount || !currentOrganization || !selectedBlueprintForDelete) {
      return
    }

    await deleteAIPlanBlueprint({ organizationId: currentOrganization.id, accountId: currentAccount.id, blueprintId: selectedBlueprintForDelete.id })

    setSelectedBlueprintForDelete(undefined)
  }

  const onCreateClick = () => {
    history.push('/ai/blueprints/create')
  }

  return (
    <EuiPage className='euiPage--platform'>
      <EuiPageBody>
        <EuiPageHeader>
          <EuiPageHeaderSection>
            <EuiTitle>
              <h1>AI Blueprints: {currentAccount?.name}</h1>
            </EuiTitle>
            <EuiText>Use blueprints to generate AI-powered market analysis and audience personas for your client.</EuiText>
          </EuiPageHeaderSection>
        </EuiPageHeader>
        <EuiPanel grow={false} className='eui-xScroll'>
          <AcEuiInMemoryTableWithSearch
            searchFields={['name']}
            message={isFetchingBlueprints ? 'Loading Blueprints...' : 'No Blueprints Found'}
            search={{
              toolsRight: [
                <EuiButton fill size={'s'} onClick={onCreateClick}>
                  Create Blueprint
                </EuiButton>
              ]
            }}
            loading={isFetchingBlueprints}
            items={isFetchingBlueprints ? [] : blueprintItems}
            columns={columns}
            sorting={sorting}
            compressed
          />
        </EuiPanel>
      </EuiPageBody>
      {processingModalOpen && (
        <AgencyV2ModalOld isLoading={false} title='AI Blueprint' onClose={() => setProcessingModalOpen(false)} confirmButtonText='OK' onConfirmButtonClick={() => setProcessingModalOpen(false)} bodyHeight='11em'>
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiText>AdCritter AI is currently building your blueprint with market analysis and audience personas.</EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiText>Due to strong demand, this can take several hours. We recommend allowing 24 hours just to be safe.</EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        </AgencyV2ModalOld>
      )}
      {selectedBlueprintForDelete && (
        <AgencyV2ModalOld isLoading={deleteAIPlanBlueprintResult.isLoading} title='Delete Blueprint' confirmButtonText='Delete' onConfirmButtonClick={onDeleteBlueprintClick} onClose={() => setSelectedBlueprintForDelete(undefined)} showCancelButton>
          <EuiText>Are you sure you want to delete this blueprint?</EuiText>
        </AgencyV2ModalOld>
      )}
      {selectedBlueprintForUnhide && (
        <AgencyV2ModalOld
          isLoading={unhideBlueprintResult.isLoading}
          title='Make Blueprint Visible'
          confirmButtonText='Make Visible'
          onConfirmButtonClick={async () => {
            if (currentOrganization && currentAccount && selectedBlueprintForUnhide) {
              await unhideBlueprint({ organizationId: currentOrganization.id, accountId: currentAccount.id, blueprintId: selectedBlueprintForUnhide.id })
              setSelectedBlueprintForUnhide(undefined)
            }
          }}
          onClose={() => setSelectedBlueprintForUnhide(undefined)}
          showCancelButton>
          <EuiText>This blueprint is currently hidden from non-staff users. Would you like to make it visible?</EuiText>
        </AgencyV2ModalOld>
      )}
    </EuiPage>
  )
}
